import { useInfiniteQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { api, type ListResponse } from 'src/common/utils/api';
import type { CalendarEvent } from 'src/meetings/models';

type CalendarEventsInput = {
  pastOrUpcoming: 'PAST' | 'UPCOMING';
  clientMeetingsOnly?: boolean;
  clientId?: string;
};

export const useCalendarEvents = ({
  pastOrUpcoming,
  clientMeetingsOnly = false,
  clientId,
}: CalendarEventsInput) => {
  const startOfMinute = new Date();
  startOfMinute.setSeconds(0, 0);

  const resource = clientId ? `clients/${clientId}/calendar-events` : 'calendar-events';
  const params = useMemo(
    () => buildParams(pastOrUpcoming, startOfMinute, clientMeetingsOnly, clientId),
    [pastOrUpcoming, startOfMinute, clientMeetingsOnly, clientId],
  );

  const { data, isLoading, error, refetch, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useInfiniteQuery<ListResponse<CalendarEvent>>({
      queryKey: [resource, params],
      queryFn: ({ pageParam = 1 }) =>
        api.getPaginated<CalendarEvent>(resource, {
          page: pageParam,
          size: 10,
          ...params,
        }),
      getNextPageParam: (lastPage, allPages) => {
        const totalFetched = allPages.reduce((sum, page) => sum + page.items.length, 0);
        return totalFetched < lastPage.total ? allPages.length + 1 : undefined;
      },
    });

  const events = data?.pages.flatMap(page => page.items) ?? [];

  return {
    events,
    isLoading,
    error,
    refetch,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  };
};

function buildParams(
  pastOrUpcoming: 'PAST' | 'UPCOMING',
  date: Date,
  clientMeetingsOnly: boolean,
  clientId?: string,
): Record<string, unknown> {
  const params: Record<string, unknown> = {
    order: pastOrUpcoming === 'PAST' ? 'desc' : 'asc',
  };

  if (pastOrUpcoming === 'PAST') {
    params.endAt_lt = date.toISOString();
  } else {
    params.endAt_gte = date.toISOString();
  }

  if (!clientId) {
    params.clientMeetingsOnly = clientMeetingsOnly;
  }

  return params;
}
